import { useSEO } from "@/hooks/useSEO";

const LegalShell = ({ title, subtitle, children, testId }) => {
  useSEO({ title: `${title} · SwaanStay`, description: subtitle });
  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-14" data-testid={testId}>
        <div className="hw-label text-blue-700">SwaanStay Legal</div>
        <h1 className="hw-head text-4xl md:text-5xl font-black text-slate-900 mt-3">{title}</h1>
        {subtitle && <p className="text-slate-600 mt-3 max-w-2xl">{subtitle}</p>}
        <div className="mt-8 hw-card p-8 md:p-10 prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-5">
          {children}
        </div>
        <div className="hw-label text-slate-500 mt-6">Last updated: February 2026</div>
      </div>
    </div>
  );
};

export function About() {
  return (
    <LegalShell
      title="About SwaanStay"
      subtitle="India's first AI-powered hotel marketplace where hotels bid for you — not the other way around."
      testId="page-about"
    >
      <p><strong>SwaanStay</strong> was founded in 2025 with a simple idea: hotel pricing should work <em>for</em> travellers, not against them. Instead of endlessly comparing listings, you post what you need — city, dates, budget — and verified hotels compete for your business with real, negotiated offers.</p>
      <h3 className="hw-head font-bold text-slate-900 text-xl">What makes us different</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Reverse bidding:</strong> Post your Group Booking Bid in 60 seconds; hotels bid live.</li>
        <li><strong>AI Auto-Bidding &amp; Mythri AI:</strong> Claude-powered concierge helps you decide when to accept, counter, or wait.</li>
        <li><strong>Verified partners:</strong> Every hotel is KYC-verified with GST, PAN and bank details on record.</li>
        <li><strong>Fair play:</strong> Server-side price recomputation; no dark patterns, no hidden fees.</li>
      </ul>
      <h3 className="hw-head font-bold text-slate-900 text-xl">Our mission</h3>
      <p>Save Indian travellers ₹1,000 crore in hotel spend by 2030 — while giving small, independent hotels a fair shot at big-city travellers without paying 20% OTA commissions.</p>
      <h3 className="hw-head font-bold text-slate-900 text-xl">The team</h3>
      <p>A small, product-obsessed team based in Bengaluru with prior stints at MakeMyTrip, OYO, Amazon and Razorpay. We&apos;re building the marketplace we&apos;d want to book from ourselves.</p>
    </LegalShell>
  );
}

export function Contact() {
  return (
    <LegalShell
      title="Contact Us"
      subtitle="Questions, feedback, partnerships or press — reach out and we'll respond within one business day."
      testId="page-contact"
    >
      <div className="grid md:grid-cols-2 gap-6">
        <div className="p-5 border border-slate-200 rounded-md">
          <div className="hw-label text-blue-700">Customer support</div>
          <div className="hw-head font-bold text-slate-900 text-lg mt-1">Support@swaanstay.com</div>
          <div className="text-sm text-slate-600 mt-1">+91 90304 60666 · WhatsApp & Call · 24×7 in English &amp; Hindi</div>
        </div>
        <div className="p-5 border border-slate-200 rounded-md">
          <div className="hw-label text-emerald-700">Hotel partnerships</div>
          <div className="hw-head font-bold text-slate-900 text-lg mt-1">partners@swaanstay.com</div>
          <div className="text-sm text-slate-600 mt-1">List your hotel · commercials · training</div>
        </div>
        <div className="p-5 border border-slate-200 rounded-md">
          <div className="hw-label text-amber-700">Press &amp; media</div>
          <div className="hw-head font-bold text-slate-900 text-lg mt-1">press@swaanstay.com</div>
        </div>
        <div className="p-5 border border-slate-200 rounded-md">
          <div className="hw-label text-slate-600">Grievance officer</div>
          <div className="hw-head font-bold text-slate-900 text-lg mt-1">grievance@swaanstay.com</div>
          <div className="text-sm text-slate-600 mt-1">As per IT Rules 2021</div>
        </div>
      </div>
      <h3 className="hw-head font-bold text-slate-900 text-xl mt-6">Registered office</h3>
      <p>Swaan Hotels &amp; Resorts Private Limited<br />Hyderabad, Telangana, India</p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell
      title="Terms & Conditions"
      subtitle="The rules of the road for using SwaanStay. Please read carefully — using the platform means you agree."
      testId="page-terms"
    >
      <h3 className="hw-head font-bold text-slate-900 text-xl">1. Acceptance</h3>
      <p>By creating an account or using SwaanStay (web or mobile), you agree to these Terms and our Privacy Policy. If you don&apos;t agree, please don&apos;t use the platform.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">2. Eligibility</h3>
      <p>You must be 18+ and legally capable of entering a contract under Indian law. Hotels partners must be legally registered businesses with valid GST/PAN.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">3. Bookings, bids &amp; payments</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>A bid from a hotel is a binding offer for the stated dates and rooms.</li>
        <li>Once you confirm and pay, the booking is locked. Cancellation follows the hotel&apos;s posted policy (visible before confirmation).</li>
        <li>All prices are shown in INR and include applicable GST (12% on room tariff below ₹7,500; 18% at or above).</li>
        <li>Payments are processed through Razorpay. SwaanStay never stores your full card / UPI credentials.</li>
      </ul>

      <h3 className="hw-head font-bold text-slate-900 text-xl">4. Refunds</h3>
      <p>Refunds are processed to the original payment method within 5-7 working days as per the hotel&apos;s cancellation policy. Wallet credits are non-transferable and expire 12 months after last use.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">5. Fair use</h3>
      <p>No scraping, no fake bookings, no impersonation, no abuse of coupons/referral programs. Violations may lead to account suspension and forfeiture of wallet balance.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">6. Liability</h3>
      <p>SwaanStay is a marketplace. The actual stay contract is between you and the hotel. Our maximum liability is capped at the amount you paid for the specific booking in dispute.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">7. Governing law</h3>
      <p>These Terms are governed by the laws of India. Courts at Bengaluru have exclusive jurisdiction.</p>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell
      title="Privacy Policy"
      subtitle="What we collect, why we collect it, and the choices you have. Written in plain English."
      testId="page-privacy"
    >
      <h3 className="hw-head font-bold text-slate-900 text-xl">1. What we collect</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Account:</strong> name, email, phone (used for OTP &amp; booking confirmations).</li>
        <li><strong>Booking data:</strong> travel dates, city preferences, guest names for the hotel.</li>
        <li><strong>Device data:</strong> IP, browser type, approximate location, cookies (for security &amp; fraud checks).</li>
        <li><strong>Payments:</strong> handled by Razorpay; SwaanStay stores only the transaction reference and last-4 digits.</li>
      </ul>

      <h3 className="hw-head font-bold text-slate-900 text-xl">2. Why we collect it</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>Deliver the service you asked for (bookings, invoices, support).</li>
        <li>Fraud prevention &amp; account security.</li>
        <li>AI-powered personalisation (Mythri AI, Auto-Bidding suggestions).</li>
        <li>Legal &amp; tax compliance.</li>
      </ul>

      <h3 className="hw-head font-bold text-slate-900 text-xl">3. Who we share it with</h3>
      <p>The hotel you book with (name, contact for check-in), our payment processor (Razorpay), our communication providers (Twilio, Resend) and law enforcement when legally compelled. We <strong>never sell</strong> personal data to advertisers.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">4. Your rights</h3>
      <p>Under the Digital Personal Data Protection Act 2023, you can access, correct, download and delete your data at any time. Write to <strong>privacy@swaanstay.com</strong>. We respond within 30 days.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">5. Cookies</h3>
      <p>We use essential cookies (login, cart) and lightweight first-party analytics. No third-party ad cookies.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">6. Retention</h3>
      <p>Booking &amp; tax records: 8 years (as required by Indian tax law). Account data: as long as your account is active + 6 months after deletion.</p>
    </LegalShell>
  );
}

export function DataProtection() {
  return (
    <LegalShell
      title="Data Protection Policy"
      subtitle="How SwaanStay protects your personal and financial information under the DPDP Act 2023."
      testId="page-data-protection"
    >
      <h3 className="hw-head font-bold text-slate-900 text-xl">1. Data Fiduciary</h3>
      <p>Swaan Hotels &amp; Resorts Private Limited is the Data Fiduciary for personal data processed on this platform. Our Data Protection Officer can be reached at <strong>Support@swaanstay.com</strong>.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">2. Lawful basis</h3>
      <p>We process personal data based on your consent, contractual necessity (bookings), and legitimate interests (fraud prevention, service improvement).</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">3. Security safeguards</h3>
      <ul className="list-disc pl-6 space-y-1">
        <li>TLS 1.3 for all data in transit.</li>
        <li>AES-256 encryption at rest for MongoDB collections.</li>
        <li>bcrypt password hashing with per-user salt.</li>
        <li>Role-based access control; least-privilege principle for staff.</li>
        <li>Quarterly penetration tests &amp; monthly dependency-vulnerability scans.</li>
      </ul>

      <h3 className="hw-head font-bold text-slate-900 text-xl">4. Breach notification</h3>
      <p>In the event of a personal data breach, we will notify the Data Protection Board of India and affected users within <strong>72 hours</strong> of confirmed detection, along with details of the breach and remedial steps.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">5. Cross-border transfers</h3>
      <p>All primary data is stored on servers in India (Mumbai region). Limited AI-processing may involve transient transfers to servers in the EU/US under Standard Contractual Clauses. No sensitive data (Aadhaar, biometric) is ever transferred abroad.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">6. Children</h3>
      <p>We do not knowingly collect data from anyone under 18. Parents/guardians who believe we might have collected their child&apos;s data can request deletion via <strong>dpo@swaanstay.com</strong>.</p>

      <h3 className="hw-head font-bold text-slate-900 text-xl">7. Complaints</h3>
      <p>If you&apos;re unhappy with how we handle your data, contact <strong>grievance@swaanstay.com</strong>. If unresolved within 30 days, you may escalate to the Data Protection Board of India.</p>
    </LegalShell>
  );
}
